import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Chip,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Participant, InviteStatus } from '../../types/generated/graphql';
import { colors } from '../../theme/colors';
import GravatarAvatar from '../GravatarAvatar';

/**
 * Selection can be an existing participant or an invite-new entry
 */
export type PersonSelection =
  | { type: 'existing'; participant: Participant }
  | { type: 'invite'; email: string; name?: string };

interface PersonPickerProps {
  /** All participants available for selection */
  participants: Participant[];
  /** Current user's email (for "Me" row) */
  currentUserEmail?: string | null;
  /** Currently selected person/invite */
  selection: PersonSelection | null;
  /** Callback when selection changes */
  onSelect: (selection: PersonSelection | null) => void;
  /** Whether to show "Me" option at top (for role/step assignment) */
  showMeOption?: boolean;
  /** Placeholder text for search */
  placeholder?: string;
  /** Accent color for selection highlight */
  accentColor?: string;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * PersonPicker - Unified person selection component
 *
 * Features:
 * - Single search input that filters participants and validates emails
 * - Shows existing participants as results
 * - When typed text is a valid email not matching any participant, shows "Invite new: {email}"
 * - Selecting invite reveals optional Name field
 * - Clear visual selection state with radio/check icons
 * - Handles case where typed email matches existing person
 */
export const PersonPicker: React.FC<PersonPickerProps> = ({
  participants,
  currentUserEmail,
  selection,
  onSelect,
  showMeOption = true,
  placeholder = 'Search name or email',
  accentColor = colors.blue[600],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteName, setInviteName] = useState('');

  // Find current user's participant record
  const meParticipant = useMemo(() => {
    if (!currentUserEmail) return null;
    return participants.find(
      (p) => p.email?.toLowerCase() === currentUserEmail.toLowerCase()
    );
  }, [participants, currentUserEmail]);

  // Filter participants based on search (excluding "me" if showing separately)
  const filteredParticipants = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    let filtered = participants;

    // Exclude "me" from main list if showing separately
    if (showMeOption && meParticipant) {
      filtered = filtered.filter((p) => p.id !== meParticipant.id);
    }

    // Filter by search query
    if (query) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.email?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [participants, searchQuery, showMeOption, meParticipant]);

  // Check if search query is a valid email
  const isValidEmail = useMemo(() => {
    return EMAIL_REGEX.test(searchQuery.trim());
  }, [searchQuery]);

  // Check if typed email matches an existing participant
  const existingMatchForEmail = useMemo(() => {
    if (!isValidEmail) return null;
    const email = searchQuery.trim().toLowerCase();
    return participants.find((p) => p.email?.toLowerCase() === email);
  }, [participants, searchQuery, isValidEmail]);

  // Determine if we should show the "Invite new" row
  const showInviteNew = useMemo(() => {
    // Show if valid email and doesn't match existing participant
    return isValidEmail && !existingMatchForEmail;
  }, [isValidEmail, existingMatchForEmail]);

  // Check if a participant is currently selected
  const isParticipantSelected = (participantId: string): boolean => {
    return selection?.type === 'existing' && selection.participant.id === participantId;
  };

  // Check if invite-new is selected with current search email
  const isInviteSelected = useMemo(() => {
    if (!selection || selection.type !== 'invite') return false;
    return selection.email.toLowerCase() === searchQuery.trim().toLowerCase();
  }, [selection, searchQuery]);

  // Handle selecting an existing participant
  const handleSelectParticipant = (participant: Participant) => {
    if (isParticipantSelected(participant.id)) {
      // Deselect
      onSelect(null);
    } else {
      onSelect({ type: 'existing', participant });
    }
  };

  // Handle selecting "Invite new"
  const handleSelectInvite = () => {
    if (isInviteSelected) {
      onSelect(null);
    } else {
      onSelect({
        type: 'invite',
        email: searchQuery.trim(),
        name: inviteName.trim() || undefined,
      });
    }
  };

  // Update invite name and propagate to selection
  const handleInviteNameChange = (name: string) => {
    setInviteName(name);
    if (selection?.type === 'invite') {
      onSelect({
        type: 'invite',
        email: selection.email,
        name: name.trim() || undefined,
      });
    }
  };

  // Get invite status styling
  const getInviteStatusChip = (status?: InviteStatus | null) => {
    if (!status || status === InviteStatus.Accepted) return null;

    const config = {
      [InviteStatus.Sent]: { label: 'Invited', color: colors.blue[600], bg: colors.blue[50] },
      [InviteStatus.Expired]: { label: 'Expired', color: colors.red[600], bg: colors.red[50] },
      [InviteStatus.NotSent]: { label: 'Not sent', color: colors.gray[500], bg: colors.gray[100] },
    };

    const style = config[status];
    if (!style) return null;

    return (
      <Chip
        label={style.label}
        size="small"
        sx={{
          height: 18,
          fontSize: '0.6rem',
          fontWeight: 600,
          bgcolor: style.bg,
          color: style.color,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  };

  // Render a person row
  const renderPersonRow = (
    participant: Participant,
    isSelected: boolean,
    isMe: boolean = false
  ) => (
    <Box
      key={participant.id}
      onClick={() => handleSelectParticipant(participant)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: 1.5,
        cursor: 'pointer',
        bgcolor: isSelected ? `${accentColor}08` : 'transparent',
        border: `1.5px solid ${isSelected ? accentColor : 'transparent'}`,
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: isSelected ? `${accentColor}12` : colors.gray[50],
        },
      }}
    >
      {/* Selection indicator */}
      {isSelected ? (
        <CheckCircleIcon sx={{ fontSize: 20, color: accentColor }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: colors.gray[300] }} />
      )}

      {/* Avatar */}
      <GravatarAvatar
        email={participant.email}
        name={isMe ? 'Me' : participant.name}
        size={36}
        showTooltip={false}
      />

      {/* Name and email */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isSelected ? 600 : 500,
            color: colors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isMe ? 'Me' : participant.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.textSecondary,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {participant.email || participant.phone || 'No contact'}
        </Typography>
      </Box>

      {/* Status chip */}
      {!isMe && getInviteStatusChip(participant.inviteStatus)}
    </Box>
  );

  return (
    <Box>
      {/* Search input */}
      <TextField
        fullWidth
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: colors.gray[400] }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: colors.gray[50],
            '&:hover': { bgcolor: colors.gray[100] },
            '&.Mui-focused': { bgcolor: 'white' },
          },
        }}
      />

      {/* Results list */}
      <Box
        sx={{
          maxHeight: 320,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {/* "Me" option */}
        {showMeOption && meParticipant && !searchQuery && (
          <>
            {renderPersonRow(meParticipant, isParticipantSelected(meParticipant.id), true)}
            {filteredParticipants.length > 0 && (
              <Box sx={{ borderBottom: `1px solid ${colors.gray[100]}`, my: 1 }} />
            )}
          </>
        )}

        {/* Existing participants */}
        {filteredParticipants.map((participant) =>
          renderPersonRow(participant, isParticipantSelected(participant.id))
        )}

        {/* "Email already exists" hint */}
        {existingMatchForEmail && searchQuery && (
          <Box
            sx={{
              py: 1,
              px: 1.5,
              bgcolor: colors.blue[50],
              borderRadius: 1.5,
              mt: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.blue[700] }}>
              This email belongs to <strong>{existingMatchForEmail.name}</strong>.
              Select them above.
            </Typography>
          </Box>
        )}

        {/* "Invite new" row */}
        {showInviteNew && (
          <Box sx={{ mt: 0.5 }}>
            <Box
              onClick={handleSelectInvite}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                px: 1.5,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: isInviteSelected ? `${accentColor}08` : colors.gray[50],
                border: `1.5px solid ${isInviteSelected ? accentColor : colors.gray[200]}`,
                borderStyle: isInviteSelected ? 'solid' : 'dashed',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isInviteSelected ? `${accentColor}12` : colors.gray[100],
                  borderColor: isInviteSelected ? accentColor : colors.gray[300],
                },
              }}
            >
              {/* Selection indicator */}
              {isInviteSelected ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: accentColor }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: colors.gray[300] }} />
              )}

              {/* Add icon */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: isInviteSelected ? `${accentColor}15` : colors.gray[200],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonAddIcon
                  sx={{ fontSize: 18, color: isInviteSelected ? accentColor : colors.gray[500] }}
                />
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isInviteSelected ? 600 : 500,
                    color: isInviteSelected ? accentColor : colors.textPrimary,
                  }}
                >
                  Invite new: {searchQuery.trim()}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  Send an invitation email
                </Typography>
              </Box>
            </Box>

            {/* Name field (shown when invite is selected) */}
            <Collapse in={isInviteSelected}>
              <Box sx={{ mt: 1.5, pl: 6.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name (optional)"
                  value={inviteName}
                  onChange={(e) => handleInviteNameChange(e.target.value)}
                  placeholder="John Doe"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Empty state */}
        {searchQuery && filteredParticipants.length === 0 && !showInviteNew && !existingMatchForEmail && (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              color: colors.textSecondary,
            }}
          >
            <Typography variant="body2">No matches found</Typography>
            {!isValidEmail && searchQuery.includes('@') && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Enter a complete email to invite someone new
              </Typography>
            )}
          </Box>
        )}

        {/* Initial empty state */}
        {!searchQuery && filteredParticipants.length === 0 && (!showMeOption || !meParticipant) && (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              color: colors.textSecondary,
            }}
          >
            <Typography variant="body2">No participants yet</Typography>
            <Typography variant="caption">
              Type an email address to invite someone
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PersonPicker;
